/**
 * InstallAppPrompt (iter1078) — „Добавете Nutramilo на началния екран".
 * Android/Chrome: истински бутон през `beforeinstallprompt`.
 * iPhone/Safari: същият бутон отваря кратки стъпки (iOS не дава системен
 * диалог на сайтовете). Показва се само на телефон и само ако приложението
 * още не е инсталирано; отказът се помни 30 дни.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Share, Plus, X, Smartphone, Download } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";

const KEY = "nm-a2hs-dismissed-at";
const SNOOZE_DAYS = 30;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
  (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);

const snoozed = () => {
  const at = Number(localStorage.getItem(KEY) || 0);
  return at > 0 && Date.now() - at < SNOOZE_DAYS * 86400000;
};

export default function InstallAppPrompt() {
  const { isDark } = useTheme();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    if (isStandalone() || snoozed() || window.innerWidth >= 768) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS никога не изпраща `beforeinstallprompt` — показваме бутона сами.
    const t = isIOS() ? setTimeout(() => setVisible(true), 2500) : null;

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(KEY, String(Date.now()));
    setVisible(false);
    setShowSteps(false);
  }, []);

  const install = useCallback(async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      if (outcome === "accepted") dismiss();
      else setVisible(false);
      return;
    }
    setShowSteps(true);
  }, [deferred, dismiss]);

  if (!visible) return null;

  const card = isDark
    ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,22%)] text-white"
    : "bg-white border-gray-200 text-[#1A1A2E]";

  return (
    <>
      <div
        data-testid="install-app-prompt"
        className={`md:hidden fixed left-3 right-3 z-[70] rounded-2xl border shadow-lg px-3 py-2.5 flex items-center gap-3 ${card}`}
        style={{ bottom: "calc(9.5rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <img src="/app-icon.png" alt="" className="w-9 h-9 rounded-xl shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight">Nutramilo на началния екран</p>
          <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            Отваря се като приложение и работи и без връзка
          </p>
        </div>
        <button
          type="button"
          onClick={install}
          data-testid="install-app-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 min-h-[40px] text-[11px] font-bold text-white bg-gradient-to-r from-[#7B1FA2] to-[#C2185B]"
        >
          <Download className="w-3.5 h-3.5" /> Добавете
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Затваряне"
          data-testid="install-app-dismiss"
          className={`shrink-0 p-2 rounded-full ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {showSteps && (
        <div
          className="md:hidden fixed inset-0 z-[80] bg-black/60 flex items-end"
          onClick={() => setShowSteps(false)}
          data-testid="install-app-steps"
        >
          <div
            className={`w-full rounded-t-3xl p-5 pb-8 ${card}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <img src="/app-icon.png" alt="" className="w-10 h-10 rounded-xl" />
              <div>
                <p className="text-base font-bold leading-tight">Три стъпки на iPhone</p>
                <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Safari не дава на сайтовете системен бутон — правите го веднъж ръчно.
                </p>
              </div>
            </div>
            <ol className="space-y-3">
              {[
                { icon: Share, text: "Натиснете „Споделяне“ в долната лента на Safari." },
                { icon: Plus, text: "Изберете „Към началния екран“ (Add to Home Screen)." },
                { icon: Smartphone, text: "Натиснете „Добавяне“ — иконата на Nutramilo се появява при приложенията ви." },
              ].map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-8 h-8 shrink-0 rounded-xl grid place-items-center bg-gradient-to-br from-[#7B1FA2] to-[#C2185B]">
                    <Icon className="w-4 h-4 text-white" />
                  </span>
                  <span className="text-sm leading-snug pt-1.5">{text}</span>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={dismiss}
              data-testid="install-steps-done"
              className="mt-5 w-full rounded-full min-h-[44px] text-sm font-bold text-white bg-gradient-to-r from-[#7B1FA2] to-[#C2185B]"
            >
              Готово
            </button>
          </div>
        </div>
      )}
    </>
  );
}
